import { z } from "zod";
import {
  // secureStorage,
  createSecureStorage,
  SecureStorageKeys,
} from "@pineseed/gui/util/secure-storage";

// Participant ID Schema - keep original for backward compatibility
export const ParticipantIdSchema = z.object({
  id: z.string().min(1),
  deviceFingerprint: z.string().min(1),
  timestamp: z.number().int().positive(),
  version: z.literal("1.0.0"),
});

const secureStorage = createSecureStorage();

export type ParticipantId = z.infer<typeof ParticipantIdSchema>;

// Storage key for secureStorage
const STORAGE_KEY = SecureStorageKeys.PARTICIPANT_ID;
const FINGERPRINT_CACHE_KEY = SecureStorageKeys.DEVICE_FINGERPRINT;

// Device fingerprint components
interface FingerprintComponents {
  canvas: string;
  webgl: string;
  screen: string;
  fonts: string;
  audio: string;
  userAgent: string;
  timezone: string;
  language: string;
  platform: string;
}

/**
 * Generate a SHA-256 hash from input string
 */
async function generateHash(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Tier 1: Canvas fingerprinting
 */
function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return "canvas-unsupported";

    canvas.width = 200;
    canvas.height = 50;

    // Draw text with different properties
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("Device fingerprint 🌲", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("Device fingerprint 🌲", 4, 17);

    // Draw arc and other shapes
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = "rgb(255,0,255)";
    ctx.beginPath();
    ctx.arc(50, 50, 50, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();

    return canvas.toDataURL();
  } catch {
    return "canvas-error";
  }
}

/**
 * Tier 1: WebGL fingerprinting
 */
function getWebGLFingerprint(): string {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") ||
      (canvas.getContext("experimental-webgl") as WebGLRenderingContext | null);
    if (!gl) return "webgl-unsupported";

    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    const vendor = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
      : "";
    const renderer = debugInfo
      ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
      : "";

    const glVersion = gl.getParameter(gl.VERSION);
    const shadingLanguageVersion = gl.getParameter(gl.SHADING_LANGUAGE_VERSION);
    const extensions = gl.getSupportedExtensions()?.join(",") || "";

    return `${vendor}|${renderer}|${glVersion}|${shadingLanguageVersion}|${extensions}`;
  } catch {
    return "webgl-error";
  }
}

/**
 * Tier 1: Screen fingerprinting
 */
function getScreenFingerprint(): string {
  const screen = window.screen;
  return `${screen.width}x${screen.height}|${screen.colorDepth}|${screen.pixelDepth}|${window.devicePixelRatio || 1}|${screen.availWidth}x${screen.availHeight}`;
}

/**
 * Tier 1: Font detection fingerprinting
 */
function getFontFingerprint(): string {
  try {
    const testFonts = [
      "Arial",
      "Times New Roman",
      "Helvetica",
      "Georgia",
      "Verdana",
      "Courier New",
      "Comic Sans MS",
      "Impact",
      "Trebuchet MS",
      "Tahoma",
      "Palatino",
      "Lucida Console",
      "Monaco",
      "Consolas",
      "Menlo",
    ];

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return "fonts-unsupported";

    const testText = "mmmmmmmmmmlli";
    const testSize = "72px";
    const baseline = "top";

    ctx.textBaseline = baseline;
    ctx.font = `${testSize} monospace`;
    const baselineWidth = ctx.measureText(testText).width;

    const availableFonts: string[] = [];

    for (const font of testFonts) {
      ctx.font = `${testSize} ${font}, monospace`;
      const width = ctx.measureText(testText).width;
      if (width !== baselineWidth) {
        availableFonts.push(font);
      }
    }

    return availableFonts.sort().join(",");
  } catch {
    return "fonts-error";
  }
}

/**
 * Tier 1: Audio fingerprinting
 */
function getAudioFingerprint(): Promise<string> {
  return new Promise((resolve) => {
    try {
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();

      // Use deterministic audio context properties instead of processing audio
      const sampleRate = audioContext.sampleRate;
      const baseLatency = audioContext.baseLatency || 0;
      const outputLatency = audioContext.outputLatency || 0;
      const state = audioContext.state;

      // Get available audio context capabilities
      const maxChannelCount = audioContext.destination.maxChannelCount;
      const channelCount = audioContext.destination.channelCount;
      const channelCountMode = audioContext.destination.channelCountMode;
      const channelInterpretation =
        audioContext.destination.channelInterpretation;

      audioContext.close();

      const audioFingerprint = `${sampleRate}|${baseLatency}|${outputLatency}|${state}|${maxChannelCount}|${channelCount}|${channelCountMode}|${channelInterpretation}`;
      resolve(audioFingerprint);
    } catch (error) {
      resolve(`audio-error: ${error}`);
    }
  });
}

/**
 * Tier 2: User Agent fingerprinting
 */
function getUserAgentFingerprint(): string {
  return navigator.userAgent || "no-useragent";
}

/**
 * Tier 2: Timezone fingerprinting
 */
function getTimezoneFingerprint(): string {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offset = new Date().getTimezoneOffset();
    return `${timezone}|${offset}`;
  } catch {
    return "timezone-error";
  }
}

/**
 * Tier 2: Language fingerprinting
 */
function getLanguageFingerprint(): string {
  const languages = navigator.languages || [navigator.language] || ["unknown"];
  return languages.join(",");
}

/**
 * Tier 2: Platform API fingerprinting
 */
function getPlatformFingerprint(): string {
  const platform = navigator.platform || "unknown";
  const hardwareConcurrency = navigator.hardwareConcurrency || 0;
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const cookieEnabled = navigator.cookieEnabled ? "1" : "0";
  const doNotTrack = navigator.doNotTrack || "unknown";

  return `${platform}|${hardwareConcurrency}|${maxTouchPoints}|${cookieEnabled}|${doNotTrack}`;
}

/**
 * Generate comprehensive device fingerprint
 */
async function generateDeviceFingerprint(): Promise<string> {
  const components: FingerprintComponents = {
    canvas: getCanvasFingerprint(),
    webgl: getWebGLFingerprint(),
    screen: getScreenFingerprint(),
    fonts: getFontFingerprint(),
    audio: await getAudioFingerprint(),
    userAgent: getUserAgentFingerprint(),
    timezone: getTimezoneFingerprint(),
    language: getLanguageFingerprint(),
    platform: getPlatformFingerprint(),
  };

  const fingerprintString = Object.entries(components)
    .map(([key, value]) => `${key}:${value}`)
    .join("|");

  return generateHash(fingerprintString);
}

/**
 * Get or generate participant ID with cross-browser resilience
 * RESTORED ORIGINAL FUNCTIONALITY - this is what the app expects
 */
export async function getParticipantId(): Promise<ParticipantId> {
  try {
    // Try to get existing ID from secureStorage
    const stored = await secureStorage.getItem(STORAGE_KEY);
    // console.log(
    //   "participant: secureStorage stored data:",
    //   stored ? "found" : "not found",
    // );

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const validated = ParticipantIdSchema.parse(parsed);
        // console.log("participant: Stored participant ID:", validated.id);
        // console.log(
        //   "participant: Stored fingerprint:",
        //   validated.deviceFingerprint.substring(0, 16) + "...",
        // );

        // Verify fingerprint still matches (for cross-browser resilience)
        const currentFingerprint = await generateDeviceFingerprint();
        // console.log(
        //   "participant: Current fingerprint:",
        //   currentFingerprint.substring(0, 16) + "...",
        // );
        // console.log(
        //   "participant: Fingerprints match:",
        //   validated.deviceFingerprint === currentFingerprint,
        // );

        if (validated.deviceFingerprint === currentFingerprint) {
          console.log("participant: Returning existing participant ID");
          return validated;
        }

        // Fingerprint changed, check cache for cross-browser scenario
        const cachedFingerprint = await secureStorage.getItem(
          FINGERPRINT_CACHE_KEY,
        );
        if (cachedFingerprint && cachedFingerprint === currentFingerprint) {
          // Current fingerprint matches cache - update stored data
          const updated: ParticipantId = {
            ...validated,
            deviceFingerprint: currentFingerprint,
          };
          await secureStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
          return updated;
        }
      } catch {
        // Invalid stored data, continue to generate new
      }
    }

    // Generate new participant ID
    console.log("participant: Generating new participant ID");
    const deviceFingerprint = await generateDeviceFingerprint();

    // Create deterministic ID from fingerprint (no randomness)
    const deterministicId = await generateHash(
      `pineseed-participant-${deviceFingerprint}`,
    );

    const participantId: ParticipantId = {
      id: deterministicId,
      deviceFingerprint,
      timestamp: Date.now(),
      version: "1.0.0",
    };
    // console.log("participant: New participant ID:", participantId.id);
    // console.log(
    //   "participant: New fingerprint:",
    //   deviceFingerprint.substring(0, 16) + "...",
    // );

    // Store in secureStorage
    await secureStorage.setItem(STORAGE_KEY, JSON.stringify(participantId));
    await secureStorage.setItem(FINGERPRINT_CACHE_KEY, deviceFingerprint);

    return participantId;
  } catch (error) {
    console.error("Error generating participant ID:", error);

    // Fallback: generate minimal ID (deterministic)
    const fallbackFingerprint = "fallback-" + navigator.userAgent.slice(0, 20);
    const fallbackId: ParticipantId = {
      id: await generateHash(`pineseed-participant-${fallbackFingerprint}`),
      deviceFingerprint: fallbackFingerprint,
      timestamp: Date.now(),
      version: "1.0.0",
    };

    try {
      await secureStorage.setItem(STORAGE_KEY, JSON.stringify(fallbackId));
    } catch {
      // secureStorage unavailable
    }

    return fallbackId;
  }
}

/**
 * Clear stored participant ID (for testing/reset)
 * RESTORED ORIGINAL FUNCTIONALITY
 */
export function clearParticipantId(): void {
  try {
    secureStorage.removeItem(STORAGE_KEY);
    secureStorage.removeItem(FINGERPRINT_CACHE_KEY);
  } catch {
    // secureStorage unavailable
  }
}

// Export schema for external validation
export { ParticipantIdSchema as participantIdSchema };

// // Export the React hook
// export { useParticipantId } from "./useParticipantId";
